import React, { useState, useEffect } from "react";
import { FiUserPlus, FiTrash2, FiEye, FiEdit2 } from "react-icons/fi";
import api from '../api/api';
import { useAuth } from '../context/AuthContext';

const ACCESS_LABEL = { View: "مشاهده", Edit: "ویرایش" };

const CalendarPermissionsPage = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [accessLevel, setAccessLevel] = useState("View");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [permsRes, usersRes] = await Promise.all([
        api.get('/calendar-permissions/'),
        api.get('/users/'),
      ]);
      setPermissions(permsRes.data);
      setAllUsers(usersRes.data.filter((u) => u.id !== user?.id));
    } catch (error) {
      console.error('خطا در دریافت دسترسی‌های تقویم:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const granted = permissions.filter((p) => p.owner === user?.id);
  const received = permissions.filter((p) => p.permitted_user === user?.id);

  const alreadyGrantedTo = (userId) => granted.some((p) => p.permitted_user === userId);

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      window.alert("لطفاً یک شخص را انتخاب کنید.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/calendar-permissions/', {
        permitted_user: Number(selectedUserId),
        access_level: accessLevel,
      });
      setSelectedUserId("");
      setAccessLevel("View");
      fetchData();
    } catch (error) {
      console.error('خطا در دادن دسترسی:', error);
      const backendMsg = error.response?.data ? JSON.stringify(error.response.data) : 'خطای نامشخص';
      window.alert('خطا در ثبت دسترسی: ' + backendMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeLevel = async (perm, newLevel) => {
    try {
      await api.patch(`/calendar-permissions/${perm.id}/`, { access_level: newLevel });
      fetchData();
    } catch (error) {
      console.error('خطا در تغییر سطح دسترسی:', error);
    }
  };

  const handleRevoke = async (permId) => {
    if (!window.confirm('آیا از حذف این دسترسی مطمئن هستید؟')) return;
    try {
      await api.delete(`/calendar-permissions/${permId}/`);
      fetchData();
    } catch (error) {
      console.error('خطا در حذف دسترسی:', error);
    }
  };

  if (loading) return <div className="p-8 text-center">در حال بارگذاری...</div>;

  return (
    <div className="p-6 bg-slate-50 min-h-screen" dir="rtl">
      <h1 className="text-xl font-bold text-slate-800 mb-2">دسترسی‌های تقویم</h1>
      <p className="text-sm text-slate-500 mb-8">
        می‌توانید به افراد دیگر اجازه دهید تقویم شما را ببینند یا ویرایش کنند، و ببینید چه کسانی به شما دسترسی داده‌اند.
      </p>

      {/* فرم افزودن دسترسی جدید */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-8">
        <h2 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
          <FiUserPlus className="text-indigo-600" /> دادن دسترسی به شخص جدید
        </h2>
        <form onSubmit={handleGrant} className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="flex-1 px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-500"
          >
            <option value="">انتخاب شخص...</option>
            {allUsers.map((u) => (
              <option key={u.id} value={u.id} disabled={alreadyGrantedTo(u.id)}>
                {u.name || u.username} {alreadyGrantedTo(u.id) ? "(قبلاً دسترسی دارد)" : ""}
              </option>
            ))}
          </select>
          <select
            value={accessLevel}
            onChange={(e) => setAccessLevel(e.target.value)}
            className="w-full sm:w-40 px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-500"
          >
            <option value="View">مشاهده</option>
            <option value="Edit">ویرایش</option>
          </select>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-indigo-600 text-white font-bold text-sm rounded-xl hover:bg-indigo-700 disabled:opacity-50"
          >
            افزودن
          </button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* دسترسی‌هایی که من داده‌ام */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-slate-800 mb-4">دسترسی‌هایی که داده‌ام</h2>
          {granted.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">هنوز دسترسی‌ای به کسی نداده‌اید.</p>
          ) : (
            <ul className="space-y-3">
              {granted.map((perm) => (
                <li key={perm.id} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600">
                      {perm.permitted_user_name?.charAt(0) || "؟"}
                    </div>
                    <span className="text-sm font-bold text-slate-700">{perm.permitted_user_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={perm.access_level}
                      onChange={(e) => handleChangeLevel(perm, e.target.value)}
                      className="text-xs px-2 py-1 border border-slate-200 rounded-lg outline-none bg-white"
                    >
                      <option value="View">مشاهده</option>
                      <option value="Edit">ویرایش</option>
                    </select>
                    <button onClick={() => handleRevoke(perm.id)} className="p-2 text-slate-400 hover:text-rose-600 transition-colors">
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* دسترسی‌هایی که به من داده شده */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h2 className="text-sm font-bold text-slate-800 mb-4">دسترسی‌هایی که به من داده شده</h2>
          {received.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">هنوز کسی به شما دسترسی نداده است.</p>
          ) : (
            <ul className="space-y-3">
              {received.map((perm) => (
                <li key={perm.id} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-600">
                      {perm.owner_name?.charAt(0) || "؟"}
                    </div>
                    <span className="text-sm font-bold text-slate-700">{perm.owner_name}</span>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-bold text-slate-500 bg-white border border-slate-200 px-3 py-1.5 rounded-lg">
                    {perm.access_level === 'Edit' ? <FiEdit2 size={12} /> : <FiEye size={12} />}
                    {ACCESS_LABEL[perm.access_level] || perm.access_level}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarPermissionsPage;
